// Sherloc Reports: prioritized analyst insights panel.
//
// Renders the additive structured analyst fields the backend attaches to a
// fresh Sherloc run. V2 is a canonical backend projection; the portal formats
// values but never re-derives strategy, trends, audience state, or findings.
// Historical reports fall through to the legacy additive analyst panel.
//
// Presentation follows the Campaign Summary / Platform Analysis card language
// rather than the diagnostic panel this started as. Every string that reaches
// the DOM is unchanged — severity, category, evidence wording and the
// Recommendation/Next step labels all still read literally — because the
// insight contract is content, not styling. Display casing is applied in CSS
// (text-transform), so the accessible text stays lowercase and intact.

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

export function extractAnalystPanelFromRunPayload(data) {
  const source = data && typeof data === "object" ? data : {};
  const analysisV2 =
    source.analysisV2 ||
    source.analysis_v2 ||
    source.run?.analysis_v2 ||
    source.run?.analysisV2 ||
    source.analysis?.analysisV2 ||
    source.analysis?.analysis_v2 ||
    null;
  if (analysisV2?.schemaVersion === "sherloc_campaign_analysis_v2") {
    return { analysisV2 };
  }
  const insights = Array.isArray(source.analystInsights)
    ? source.analystInsights.filter((insight) => insight && typeof insight === "object")
    : [];
  const limitations = Array.isArray(source.limitations)
    ? source.limitations.map((line) => String(line || "").trim()).filter(Boolean)
    : [];
  const watchlist = Array.isArray(source.watchlist)
    ? source.watchlist.map((line) => String(line || "").trim()).filter(Boolean)
    : [];
  const evidenceStrength = typeof source.evidenceStrength === "string" ? source.evidenceStrength : null;

  if (!insights.length && !limitations.length && !watchlist.length) return null;

  const sorted = [...insights].sort(
    (a, b) => (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3)
  );

  return { insights: sorted, limitations, watchlist, evidenceStrength };
}

function priorityClass(priority) {
  if (priority === "high") return "sherloc-analyst-priority sherloc-analyst-priority-high";
  if (priority === "medium") return "sherloc-analyst-priority sherloc-analyst-priority-medium";
  return "sherloc-analyst-priority sherloc-analyst-priority-low";
}

/**
 * One insight, read top-down: how urgent, what it is called, what was found,
 * what it means, what to do, what happens next. The supporting dimensions
 * (category, impact area, evidence strength) sit under the title as secondary
 * metadata instead of competing with it as a row of equal-weight badges.
 */
function InsightCard({ insight }) {
  const metadata = [
    insight.category ? { key: "category", text: insight.category } : null,
    insight.impactArea ? { key: "impact", text: insight.impactArea } : null,
    insight.evidenceStrength
      ? { key: "evidence", text: `evidence: ${insight.evidenceStrength}`, evidence: true }
      : null,
  ].filter(Boolean);

  return (
    <article className="sherloc-analyst-insight" data-priority={insight.priority || "low"}>
      <div className="sherloc-analyst-insight-head">
        <span className={priorityClass(insight.priority)}>{insight.priority || "low"}</span>
        {insight.title ? <h4 className="sherloc-analyst-insight-title">{insight.title}</h4> : null}
      </div>

      {metadata.length ? (
        <div className="sherloc-analyst-insight-meta">
          {metadata.map((item) => (
            <span
              key={item.key}
              className={
                item.evidence
                  ? "sherloc-analyst-tag sherloc-analyst-tag-evidence"
                  : "sherloc-analyst-tag"
              }
            >
              {item.text}
            </span>
          ))}
        </div>
      ) : null}

      <div className="sherloc-analyst-insight-body">
        {insight.finding ? (
          <p className="sherloc-analyst-insight-finding">{insight.finding}</p>
        ) : null}
        {insight.interpretation ? (
          <p className="sherloc-analyst-insight-interpretation">{insight.interpretation}</p>
        ) : null}
        {insight.recommendation ? (
          <p className="sherloc-analyst-insight-recommendation">
            <strong>Recommendation:</strong> {insight.recommendation}
          </p>
        ) : null}
        {insight.nextStep ? (
          <p className="sherloc-analyst-insight-next">
            <strong>Next step:</strong> {insight.nextStep}
          </p>
        ) : null}
      </div>
    </article>
  );
}

/** Data limitations and watchlist: same card language, small content, no drama. */
function NoteList({ testId, title, lines }) {
  return (
    <section className="sherloc-analyst-section sherloc-analyst-notes" data-testid={testId}>
      <h3 className="sherloc-analyst-notes-title">{title}</h3>
      <ul className="sherloc-analyst-notes-list">
        {lines.map((line) => (
          <li key={line}>
            <span className="sherloc-analyst-notes-dot" aria-hidden="true" />
            <span className="sherloc-analyst-notes-text">{line}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

const V2_SECTION_LABELS = {
  EXECUTIVE_PERFORMANCE_READ: "Executive Performance Read",
  PERFORMANCE_VS_STRATEGY: "Performance vs Strategy",
  WHAT_CHANGED: "What Changed",
  PLATFORM_CONTRIBUTION: "Platform Contribution",
  AUDIENCE_INTELLIGENCE: "Audience Intelligence",
  CROSS_PLATFORM_SYNTHESIS: "Cross-Platform Synthesis",
  OPPORTUNITIES_WATCHOUTS: "Opportunities / Watchouts",
  MEASUREMENT_CONFIDENCE: "Measurement & Confidence",
};

function humanizeKey(value) {
  return String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatFactValue(key, value, metricContext = null) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value !== "number") {
    const asText = String(value);
    return /^[A-Z0-9_]+$/.test(asText) ? humanizeKey(asText.toLowerCase()) : asText;
  }
  const normalizedKey = String(metricContext || key || "").toLowerCase();
  if (
    normalizedKey.includes("contributionshares") ||
    normalizedKey.includes("percent") ||
    normalizedKey.endsWith("rate") ||
    ["ctr", "engagementrate", "videoviewrate", "completionrate", "conversionrate"].includes(
      normalizedKey
    )
  ) {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  }
  if (
    normalizedKey.includes("spend") ||
    normalizedKey.includes("cost") ||
    normalizedKey.includes("budget") ||
    normalizedKey.includes("amount") ||
    ["cpc", "cpm", "cpa", "cpv", "conversionvalue"].includes(normalizedKey)
  ) {
    return value.toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    });
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function FactRows({ value, prefix = "", metricContext = null }) {
  if (!value || typeof value !== "object") return null;
  const localMetric = typeof value.metric === "string" ? value.metric : metricContext;
  const entries = Array.isArray(value)
    ? value.map((entry, index) => [String(index + 1), entry])
    : Object.entries(value);
  return (
    <dl className="sherloc-v2-facts">
      {entries.map(([key, item]) => {
        const path = prefix ? `${prefix}.${key}` : key;
        if (item && typeof item === "object") {
          return (
            <div className="sherloc-v2-fact-group" key={path}>
              <dt>{Array.isArray(value) ? `Evidence ${Number(key)}` : humanizeKey(key)}</dt>
              <dd><FactRows value={item} prefix={path} metricContext={localMetric} /></dd>
            </div>
          );
        }
        return (
          <div className="sherloc-v2-fact" key={path}>
            <dt>{humanizeKey(key)}</dt>
            <dd>{formatFactValue(path, item, localMetric)}</dd>
          </div>
        );
      })}
    </dl>
  );
}

function V2Finding({ finding }) {
  return (
    <article className="sherloc-analyst-insight sherloc-v2-finding">
      <div className="sherloc-analyst-insight-head">
        <span className="sherloc-analyst-tag sherloc-analyst-tag-evidence">
          {String(finding.confidence || "insufficient").toLowerCase()} confidence
        </span>
        <h4 className="sherloc-analyst-insight-title">{finding.title}</h4>
      </div>
      {Array.isArray(finding.facts) && finding.facts.length ? (
        <FactRows value={finding.facts} />
      ) : null}
      {finding.interpretation ? (
        <p className="sherloc-analyst-insight-interpretation">{finding.interpretation}</p>
      ) : null}
      {finding.action ? (
        <p className="sherloc-analyst-insight-recommendation">
          <strong>Operator consideration:</strong> {finding.action}
        </p>
      ) : null}
    </article>
  );
}

function V2Panel({ analysis }) {
  const sections = Array.isArray(analysis?.sections) ? analysis.sections : [];
  return (
    <div className="sherloc-analyst-panel sherloc-v2-panel" data-testid="sherloc-analysis-v2">
      <div className="sherloc-v2-contract-row">
        <span className="sherloc-analyst-tag sherloc-analyst-tag-evidence">
          Strategy + KPI + trend + audience intelligence
        </span>
      </div>
      {sections.map((section) => {
        const findings = Array.isArray(section.findings) ? section.findings : [];
        return (
          <section
            className="sherloc-analyst-section sherloc-v2-section"
            data-section={section.section}
            key={section.section}
          >
            <div className="sherloc-v2-section-heading">
              <h3>{V2_SECTION_LABELS[section.section] || humanizeKey(section.section)}</h3>
              {section.state !== "AVAILABLE" ? (
                <span className="sherloc-analyst-tag">{humanizeKey(section.state)}</span>
              ) : null}
            </div>
            {findings.length ? (
              <div className="sherloc-analyst-insight-list">
                {findings.map((finding) => (
                  <V2Finding key={finding.id} finding={finding} />
                ))}
              </div>
            ) : (
              <p className="sherloc-v2-unavailable">
                No governed evidence is available for this section in the selected window.
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}

export default function SherlocAnalystInsightsPanel({ panel }) {
  if (!panel) return null;
  if (panel.analysisV2) return <V2Panel analysis={panel.analysisV2} />;
  const { insights = [], limitations = [], watchlist = [], evidenceStrength = null } = panel;
  if (!insights.length && !limitations.length && !watchlist.length) return null;

  return (
    <div className="sherloc-analyst-panel" data-testid="sherloc-analyst-panel">
      {insights.length ? (
        <section className="sherloc-analyst-section" data-testid="sherloc-analyst-insights">
          {/* The page already titles this section; repeating it inside the card
              was the duplicate "Analyst Insights" heading. Only the report-level
              evidence qualifier belongs here. */}
          {evidenceStrength ? (
            <div className="sherloc-analyst-section-head">
              <span className="sherloc-analyst-tag sherloc-analyst-tag-evidence">
                report evidence: {evidenceStrength}
              </span>
            </div>
          ) : null}
          <div className="sherloc-analyst-insight-list">
            {insights.map((insight, index) => (
              <InsightCard
                key={`${insight.title || insight.finding || "insight"}-${index}`}
                insight={insight}
              />
            ))}
          </div>
        </section>
      ) : null}
      {limitations.length ? (
        <NoteList
          testId="sherloc-analyst-limitations"
          title="Data limitations"
          lines={limitations}
        />
      ) : null}
      {watchlist.length ? (
        <NoteList testId="sherloc-analyst-watchlist" title="Watchlist" lines={watchlist} />
      ) : null}
    </div>
  );
}
